/**
 * Challenges (IPM, wiki: New Starters guide — "Solo, how big a galaxy can you
 * build in 24 hr?"), the Matrix's only source of Void Shards until online
 * tournaments exist (GDD §5.6).
 *
 * After your second Fold you can start one at a time. It is a separate,
 * fresh galaxy that runs alongside your main one for 24 hours of game clock
 * (so it keeps going offline). Your permanent bonuses (Rooms, Matrix,
 * Prospectors) apply. When it ends — or you end it early — it pays VS by
 * the Galaxy Value it reached. Tournaments can reuse the same payout later.
 *
 * Only one run is "active" at a time (state.run); the other waits in
 * state.challenge.other and is simulated by swapping it in briefly.
 */

import type { Emit } from './events';
import { newRun, type GameState, type RunState } from './state';

export const CHALLENGE_HOURS = 24;
/** IPM opens challenges after you've sold two galaxies. */
export const CHALLENGE_UNLOCK_FOLDS = 2;

/** [Galaxy Value reached, VS paid] — the best tier reached pays. Provisional (GDD §12). */
export const CHALLENGE_TIERS: readonly (readonly [number, number])[] = [
  [1e6, 3], [1e7, 6], [1e8, 12], [1e9, 20], [1e10, 30], [1e11, 45], [1e12, 65],
];

export function challengeVs(galaxyValue: number): number {
  let vs = 0;
  for (const [value, pay] of CHALLENGE_TIERS) if (galaxyValue >= value) vs = pay;
  return vs;
}

export function challengeUnlocked(state: GameState): boolean {
  return state.folds >= CHALLENGE_UNLOCK_FOLDS;
}

export function canStartChallenge(state: GameState): boolean {
  return challengeUnlocked(state) && !state.challenge && state.clock >= state.challengeReadyAt;
}

export function startChallenge(state: GameState): boolean {
  if (!canStartChallenge(state)) return false;
  const seconds = CHALLENGE_HOURS * 3600;
  state.challenge = { startedAt: state.clock, endsAt: state.clock + seconds, viewing: 'main', other: newRun(null, true) };
  // One a day: the next can start once this one's 24 hours are up, however it ends.
  state.challengeReadyAt = state.clock + seconds;
  return true;
}

/** The challenge galaxy, wherever it currently sits. */
export function challengeRun(state: GameState): RunState | null {
  const c = state.challenge;
  if (!c) return null;
  return c.viewing === 'challenge' ? state.run : c.other;
}

function swapRuns(state: GameState): void {
  const c = state.challenge!;
  const active = state.run;
  state.run = c.other;
  c.other = active;
}

/** Switches which galaxy you see and control. */
export function viewGalaxy(state: GameState, which: 'main' | 'challenge'): boolean {
  const c = state.challenge;
  if (!c || c.viewing === which) return false;
  swapRuns(state);
  c.viewing = which;
  return true;
}

/** Runs `fn` with the background galaxy swapped in, then swaps back. */
export function withBackgroundRun(state: GameState, fn: () => void): void {
  if (!state.challenge) return;
  swapRuns(state);
  try { fn(); } finally { swapRuns(state); }
}

export function finishChallenge(state: GameState, emit: Emit): boolean {
  const run = challengeRun(state);
  if (!run) return false;
  if (state.challenge!.viewing === 'challenge') viewGalaxy(state, 'main');
  const vs = challengeVs(run.galaxyValue);
  state.vs += vs;
  state.challengesDone += 1;
  state.bestChallenge = Math.max(state.bestChallenge, run.galaxyValue);
  state.challenge = null;
  emit({ kind: 'challenge', value: run.galaxyValue, vs });
  return true;
}

export function stepChallenge(state: GameState, emit: Emit): void {
  if (state.challenge && state.clock >= state.challenge.endsAt) finishChallenge(state, emit);
}
